// utils/referral.js
//
// Esdras Referral Engine
//
// USER reward:    1 free bonus scan per confirmed new-user signup (not per booking)
//                 Stored as bonusScans: increment(1) on users/{referrerId}
//                 The scan pipeline reads (lifetimeScansUsed - bonusScans) to determine
//                 whether the free quota is exhausted.
//
// STYLIST reward: Platform commission reduction, max 3% (was 5%),
//                 1% per every 5 confirmed referrals.
//                 e.g. 5 refs → -1%, 10 → -2%, 15 → -3% (capped).
//
// TRIGGER POINT:  triggerReferrerReward() is called on SIGNUP CONFIRMATION,
//                 not on first booking. Call it immediately after the user's
//                 account is created and email-verified (or after phone OTP).

import { doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Extracts the referral ID from the current URL.
 * Added to fix "Attempted import error" during build.
 */
export const getReferralIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('ref');
};

// ─────────────────────────────────────────────────────────────────────────────
//  handleReferralLink()
//
//  Call this on app load after a user signs up via a referral URL.
//  Reads the ?ref= query param, validates it, and writes referrerId to the
//  new user's Firestore document so triggerReferrerReward() can find it later.
//
//  Safe to call multiple times — idempotent (only writes if referrerId absent).
// ─────────────────────────────────────────────────────────────────────────────
export const handleReferralLink = async (newUserId) => {
  const urlParams  = new URLSearchParams(window.location.search);
  const referrerId = urlParams.get('ref');

  if (!referrerId || referrerId === newUserId) return;

  try {
    const userRef  = doc(db, 'users', newUserId);
    const userSnap = await getDoc(userRef);

    // Only write if the user doesn't already have a referrerId (prevent overwrite)
    if (userSnap.exists() && !userSnap.data().referrerId) {
      await updateDoc(userRef, { referrerId });
    }

    // Clean the URL so the ref param doesn't persist on refresh
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (err) {
    console.error('[Referral] handleReferralLink error:', err);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  triggerReferrerReward()
//
//  Call this immediately after a new user's signup is confirmed
//  (e.g. after email verification or OTP success — NOT after first booking).
//
//  Rewards:
//    USER referrer  → bonusScans + 1  (grants one extra free scan)
//    STYLIST referrer → commissionReduction recalculated (max 3%)
//
//  Returns true if a reward was issued, false otherwise.
// ─────────────────────────────────────────────────────────────────────────────
export const triggerReferrerReward = async (newUserId) => {
  try {
    const newUserSnap = await getDoc(doc(db, 'users', newUserId));
    if (!newUserSnap.exists()) return false;

    const newUserData = newUserSnap.data();

    // Already rewarded — don't double-count
    if (newUserData.referralRewardIssued) return false;

    const referrerId = newUserData.referrerId;
    if (!referrerId) return false;

    // Determine whether the referrer is a regular user or a stylist
    const [uSnap, sSnap] = await Promise.all([
      getDoc(doc(db, 'users',   referrerId)),
      getDoc(doc(db, 'barbers', referrerId)),
    ]);

    let rewardIssued = false;

    // ── USER REFERRER → 1 bonus scan ────────────────────────────────────────
    if (uSnap.exists()) {
      await updateDoc(doc(db, 'users', referrerId), {
        bonusScans:          increment(1),      // each +1 allows one extra free scan
        successfulReferrals: increment(1),
        lastReferralAt:      serverTimestamp(),
      });
      rewardIssued = true;
      console.log(`[Referral] +1 bonus scan granted to user ${referrerId}`);
    }

    // ── STYLIST REFERRER → commission reduction (max 3%) ────────────────────
    if (sSnap.exists()) {
      const sData    = sSnap.data();
      const newCount = (sData.successfulReferrals || 0) + 1;

      // 1% reduction per 5 referrals, hard-capped at 3%
      const newReduction = Math.min(Math.floor(newCount / 5), 3);

      await updateDoc(doc(db, 'barbers', referrerId), {
        successfulReferrals:  increment(1),
        commissionReduction:  newReduction,     // absolute value, not incremental
        lastReferralAt:       serverTimestamp(),
      });
      rewardIssued = true;
      console.log(`[Referral] Stylist ${referrerId} commission reduction → ${newReduction}%`);
    }

    // Mark new user so they can't trigger rewards again
    if (rewardIssued) {
      await updateDoc(doc(db, 'users', newUserId), {
        referralRewardIssued: true,
        referralRewardAt:     serverTimestamp(),
      });
    }

    return rewardIssued;
  } catch (err) {
    console.error('[Referral] triggerReferrerReward error:', err);
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  getScanQuota()
//
//  Helper used by useSubscription / Scan.jsx to compute effective free quota.
//  Bonus scans from referrals extend the user's free allowance by 1 per referral.
//
//  Returns the number of scans the user is still allowed for free.
// ─────────────────────────────────────────────────────────────────────────────
export const getScanQuota = (userData) => {
  const BASE_FREE_SCANS = 1;
  const bonusScans      = userData?.bonusScans       || 0;
  const usedScans       = userData?.lifetimeScansUsed || 0;
  const totalAllowed    = BASE_FREE_SCANS + bonusScans;
  return Math.max(0, totalAllowed - usedScans);
};
